import { useCallback, useEffect, useState } from 'react';
import { StorePreferenceCreateRequest } from '@tiendanube/common';
import { useStorage } from 'commons/hooks';
import {
  useCanEnterAdmin,
  useGetStoreInfo,
  useIsLogged,
} from 'domains/Auth/hooks';
import useRefreshInfoStoreUser from 'domains/Auth/hooks/useRefreshInfoToSync';
import {
  useCreateQuestionnaireStorePreferencesMutation,
  useLazyGetQuestionnaireStatusQuery,
} from '../pages/QuestionnairePage/preOnboardingApi';

const PRE_ONBOARDING_QUESTIONNAIRE_COMPLETED =
  'pre-onboarding-questionnaire-completed';

const PRE_ONBOARDING_RELEASE_DATE = '2025-05-05';

export function useQuestionnaireStatus() {
  const [
    getQuestionnaireStatus,
    { data: questionnaireStatus, isLoading, isError, isUninitialized },
  ] = useLazyGetQuestionnaireStatusQuery();
  const canEnterAdmin = useCanEnterAdmin();
  const { isLogin } = useIsLogged();
  const { refreshInfo } = useRefreshInfoStoreUser();

  const { id, createdAt } = useGetStoreInfo();

  const isStoreCreationBeforePreOnb =
    new Date(createdAt) < new Date(PRE_ONBOARDING_RELEASE_DATE);

  const localStorageItem = `${PRE_ONBOARDING_QUESTIONNAIRE_COMPLETED}-${id}`;
  const [questionnaireCompleted, setQuestionnaireCompleted, loaded] =
    useStorage(localStorageItem, false);

  const [showQuestionnaire, setShowQuestionnaire] = useState(false);

  const [createStoreQuestionnaire] =
    useCreateQuestionnaireStorePreferencesMutation();

  const onCompleteQuestionnaire = useCallback(
    (payload: StorePreferenceCreateRequest) =>
      createStoreQuestionnaire(payload)
        .unwrap()
        .then(() => {
          getQuestionnaireStatus();
          refreshInfo(true);
        }) as Promise<void>,
    [createStoreQuestionnaire, getQuestionnaireStatus, refreshInfo],
  );

  useEffect(() => {
    if (
      isStoreCreationBeforePreOnb ||
      (!isLoading &&
        (questionnaireStatus === 'completed' || questionnaireStatus === 'skip'))
    ) {
      setQuestionnaireCompleted(true);
    }
  }, [
    questionnaireStatus,
    setQuestionnaireCompleted,
    isLoading,
    isStoreCreationBeforePreOnb,
  ]);

  useEffect(() => {
    if (
      isLogin &&
      !isStoreCreationBeforePreOnb &&
      loaded &&
      !isError &&
      !questionnaireCompleted
    ) {
      getQuestionnaireStatus();
    }
  }, [
    getQuestionnaireStatus,
    questionnaireCompleted,
    loaded,
    isError,
    isStoreCreationBeforePreOnb,
    isLogin,
  ]);

  useEffect(() => {
    if (
      (loaded && questionnaireCompleted) ||
      isError ||
      !canEnterAdmin ||
      isStoreCreationBeforePreOnb
    ) {
      setShowQuestionnaire(false);
    } else {
      setShowQuestionnaire(
        isUninitialized ||
          isLoading ||
          (questionnaireStatus === 'pending' && !questionnaireCompleted),
      );
    }
  }, [
    isError,
    isUninitialized,
    isLoading,
    questionnaireCompleted,
    questionnaireStatus,
    loaded,
    isStoreCreationBeforePreOnb,
    canEnterAdmin,
  ]);

  const skipQuestionnaire = useCallback(() => setShowQuestionnaire(false), []);

  return {
    showQuestionnaire,
    isLoadingQuestionnaire: isLoading || !loaded,
    skipQuestionnaire,
    onCompleteQuestionnaire,
  };
}
